import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Text,
} from "@radix-ui/themes";
import {
  Database,
  RefreshCw,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
} from "lucide-react";
import PageTemplate from "@/components/page-template/PageTemplate";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { searchService } from "@/services/breeze/SearchService";
import { entityService } from "@/services/breeze/EntityService";
import { EntityIndexingStatus } from "@/types/breeze/Entity";
import { useConfirm } from "@/providers/ConfirmDialogProvider";

export default withTenantAdminRequired(function OperationsPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { data: entities } = useQuery({
    queryKey: ["entities-status"],
    queryFn: () => entityService.getAll(),
    refetchInterval: (query) => {
      const isIndexing = query.state.data?.some(
        (e) => e.indexing_status === EntityIndexingStatus.InProgress,
      );
      return isIndexing ? 3000 : false;
    },
  });

  const rehydrateMutation = useMutation({
    mutationFn: () => searchService.rehydrate(),
    onSuccess: () => {
      toast.success("Search data rehydration has been initiated");
      queryClient.invalidateQueries({ queryKey: ["entities-status"] });
    },
    onError: () => {
      toast.error("Failed to initiate search data rehydration");
    },
  });

  const purgeMutation = useMutation({
    mutationFn: () => searchService.purge(),
    onSuccess: () => {
      toast.success("Search data purge has been initiated");
      queryClient.invalidateQueries({ queryKey: ["entities-status"] });
    },
    onError: () => {
      toast.error("Failed to initiate search data purge");
    },
  });

  const handlePurgeAll = async () => {
    const confirmed = await confirm({
      title: "Purge All Search Data",
      message:
        "Are you sure you want to purge all search data? This will clear your entire search index. This action cannot be undone.",
      confirmText: "Purge All",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      purgeMutation.mutate();
    }
  };

  const handleRehydrateAll = async () => {
    const confirmed = await confirm({
      title: "Rehydrate All Search Data",
      message:
        "Are you sure you want to rehydrate all search data? This will reprocess all entities and may take some time.",
      confirmText: "Rehydrate All",
      cancelText: "Cancel",
      variant: "info",
    });

    if (confirmed) {
      rehydrateMutation.mutate();
    }
  };

  const rehydrateEntityMutation = useMutation({
    mutationFn: (entityName: string) =>
      searchService.rehydrateEntity(entityName),
    onSuccess: (_, entityName) => {
      toast.success(`Rehydration initiated for ${entityName}`);
      queryClient.invalidateQueries({ queryKey: ["entities-status"] });
    },
    onError: (_, entityName) => {
      toast.error(`Failed to initiate rehydration for ${entityName}`);
    },
  });

  const purgeEntityMutation = useMutation({
    mutationFn: (entityName: string) => searchService.purgeEntity(entityName),
    onSuccess: (_, entityName) => {
      toast.success(`Purge initiated for ${entityName}`);
      queryClient.invalidateQueries({ queryKey: ["entities-status"] });
    },
    onError: (_, entityName) => {
      toast.error(`Failed to initiate purge for ${entityName}`);
    },
  });

  const handlePurgeEntity = async (entityName: string) => {
    const confirmed = await confirm({
      title: "Purge Entity Search Data",
      message: `Are you sure you want to purge all search data for "${entityName}"? This action cannot be undone.`,
      confirmText: "Purge",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      purgeEntityMutation.mutate(entityName);
    }
  };

  const handleRehydrateEntity = async (entityName: string) => {
    const confirmed = await confirm({
      title: "Rehydrate Entity Search Data",
      message: `Are you sure you want to rehydrate search data for "${entityName}"?`,
      confirmText: "Rehydrate",
      cancelText: "Cancel",
      variant: "info",
    });

    if (confirmed) {
      rehydrateEntityMutation.mutate(entityName);
    }
  };

  const operationSections = [
    {
      title: "Search Operations",
      description: "Manage search index data",
      icon: Database,
      items: [
        {
          name: "Rehydrate Search Data",
          description:
            "Rebuild the search index by reindexing all data from the database",
          icon: RefreshCw,
          action: handleRehydrateAll,
          loading: rehydrateMutation.isPending,
          variant: "soft" as const,
        },
        {
          name: "Purge Search Data",
          description: "Clear all data from the search index",
          icon: Trash2,
          action: handlePurgeAll,
          loading: purgeMutation.isPending,
          variant: "soft" as const,
          color: "red" as const,
        },
      ],
    },
  ];

  return (
    <PageTemplate
      title="Search Operations"
      description="Manage search indexing, embeddings, and maintenance"
    >
      <Flex direction="column" gap="6">
        {operationSections.map((section) => (
          <Card key={section.title} size="3">
            <Box p="1">
              <Flex align="center" gap="3" mb="4">
                <section.icon
                  className="w-6 h-6"
                  style={{ color: "var(--accent-9)" }}
                />
                <Box>
                  <Heading as="h2" size="5" weight="medium">
                    {section.title}
                  </Heading>
                  <Text size="2" color="gray" mt="1">
                    {section.description}
                  </Text>
                </Box>
              </Flex>

              <Flex direction="column" gap="3">
                {section.items.map((item) => (
                  <Box
                    key={item.name}
                    p="4"
                    style={{
                      border: "1px solid var(--gray-6)",
                      borderRadius: "var(--radius-3)",
                    }}
                  >
                    <Flex align="center" justify="between">
                      <Flex align="center" gap="3">
                        <item.icon
                          className="w-5 h-5"
                          style={{ color: "var(--gray-11)" }}
                        />
                        <Box>
                          <Heading as="h3" size="3" weight="medium">
                            {item.name}
                          </Heading>
                          <Text size="2" color="gray" mt="1">
                            {item.description}
                          </Text>
                        </Box>
                      </Flex>
                      <Button
                        onClick={item.action}
                        variant={item.variant}
                        color={item.color}
                        disabled={item.loading}
                      >
                        {item.loading ? "Processing..." : "Execute"}
                      </Button>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Card>
        ))}

        {entities && entities.length > 0 && (
          <Card size="3">
            <Box p="1">
              <Flex align="center" gap="3" mb="4">
                <RefreshCw
                  className={`w-6 h-6 ${entities.some((e) => e.indexing_status === EntityIndexingStatus.InProgress) ? "animate-spin" : ""}`}
                  style={{ color: "var(--accent-9)" }}
                />
                <Box>
                  <Heading as="h2" size="5" weight="medium">
                    Indexing Status
                  </Heading>
                  <Text size="2" color="gray" mt="1">
                    Current status of search indices per entity
                  </Text>
                </Box>
              </Flex>

              <Flex direction="column" gap="2">
                {entities
                  .filter((e) => !e.is_junction)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((entity) => (
                    <Flex
                      direction={"column"}
                      key={entity.id}
                      p="3"
                      style={{
                        border: "1px solid var(--gray-5)",
                        borderRadius: "var(--radius-3)",
                        backgroundColor: "var(--gray-2)",
                      }}
                    >
                      <Flex align="center" justify="between">
                        <Flex direction="column" align="start" gap="2">
                          <Flex align="center" gap="2">
                            <Text weight="medium" size="2">
                              {entity.name}
                            </Text>
                          </Flex>
                          <Flex direction={"row"} align={"center"} gap="2">
                            <StatusBadge status={entity.indexing_status} />
                            <Box>
                              {entity.last_indexed_at ? (
                                <Text size="1" color="gray">
                                  Last indexed:{" "}
                                  {new Date(
                                    entity.last_indexed_at,
                                  ).toLocaleString()}
                                </Text>
                              ) : (
                                <Text
                                  size="1"
                                  color="gray"
                                  style={{ fontStyle: "italic" }}
                                >
                                  {entity.indexing_status ===
                                  EntityIndexingStatus.InProgress
                                    ? ""
                                    : "Never indexed"}
                                </Text>
                              )}
                            </Box>
                          </Flex>
                        </Flex>

                        <Flex gap="2">
                          <Button
                            size="1"
                            variant="soft"
                            onClick={() => handleRehydrateEntity(entity.name)}
                            disabled={
                              (rehydrateEntityMutation.isPending &&
                                rehydrateEntityMutation.variables ===
                                  entity.name) ||
                              entity.indexing_status ===
                                EntityIndexingStatus.InProgress
                            }
                          >
                            <RefreshCw
                              className={`w-3 h-3 mr-1 ${rehydrateEntityMutation.isPending && rehydrateEntityMutation.variables === entity.name ? "animate-spin" : ""}`}
                            />
                            {rehydrateEntityMutation.isPending &&
                            rehydrateEntityMutation.variables === entity.name
                              ? "Queuing..."
                              : "Rehydrate"}
                          </Button>
                          <Button
                            size="1"
                            variant="soft"
                            color="red"
                            onClick={() => handlePurgeEntity(entity.name)}
                            disabled={
                              (purgeEntityMutation.isPending &&
                                purgeEntityMutation.variables ===
                                  entity.name) ||
                              entity.indexing_status ===
                                EntityIndexingStatus.InProgress
                            }
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            {purgeEntityMutation.isPending &&
                            purgeEntityMutation.variables === entity.name
                              ? "Queuing..."
                              : "Purge"}
                          </Button>
                        </Flex>
                      </Flex>
                    </Flex>
                  ))}
              </Flex>
            </Box>
          </Card>
        )}
      </Flex>
    </PageTemplate>
  );
});

function StatusBadge({ status }: { status: EntityIndexingStatus }) {
  switch (status) {
    case EntityIndexingStatus.InProgress:
      return (
        <Badge color="blue" variant="soft">
          <Clock className="w-3 h-3 mr-1" />
          Indexing
        </Badge>
      );
    case EntityIndexingStatus.Failed:
      return (
        <Badge color="red" variant="soft">
          <AlertCircle className="w-3 h-3 mr-1" />
          Failed
        </Badge>
      );
    case EntityIndexingStatus.Idle:
    default:
      return (
        <Badge color="green" variant="soft">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Ready
        </Badge>
      );
  }
}
